import { EditorStarterAsset } from '../assets/assets';
import { EditorStarterItem } from '../items/item-type';
import { getCropFromItem } from './get-crop-from-item';

export const byDefaultKeepAspectRatioMap: Record<
  EditorStarterItem['type'],
  boolean
> = {
  image: true,
  gif: true,
  webp: true,
  text: false,
  video: true,
  solid: false,
  blur: false,
  pdf: true,
  ppt: true,
  audio: false,
  captions: false,
  code: false,
  group: false,
};

export const canKeepAspectRatioMap: Record<EditorStarterItem['type'], boolean> =
  {
    image: true,
    gif: true,
    webp: true,
    video: true,
    solid: true,
    blur: true,
    captions: false,
    pdf: true,
    ppt: true,
    audio: false,
    text: false,
    code: false,
    group: false,
  };

export const getKeepAspectRatio = (item: EditorStarterItem) => {
  if (
    item.type === 'captions' ||
    item.type === 'audio' ||
    item.type === 'text'
  ) {
    return false;
  }

  if (item.type === 'code') {
    return false;
  }

  if (item.type === 'group') {
    return false;
  }

  if (
    item.type === 'gif' ||
    item.type === 'webp' ||
    item.type === 'image' ||
    item.type === 'video' ||
    item.type === 'pdf' ||
    item.type === 'ppt' ||
    item.type === 'solid' ||
    item.type === 'blur'
  ) {
    return item.keepAspectRatio;
  }

  throw new Error(
    `Unhandled item type: ${JSON.stringify(item satisfies never)}`,
  );
};

const getAspectRatioFromAsset = (asset: EditorStarterAsset) => {
  if (asset.type === 'image') {
    return asset.width / asset.height;
  }

  if (asset.type === 'video') {
    return asset.width / asset.height;
  }

  if (asset.type === 'gif') {
    return asset.width / asset.height;
  }

  if (asset.type === 'webp') {
    return asset.width / asset.height;
  }

  if (asset.type === 'caption') {
    return null;
  }

  if (asset.type === 'audio') {
    return null;
  }

  if (asset.type === 'pdf') {
    return asset.pageWidth / asset.pageHeight;
  }

  throw new Error(`Unhandled asset type: ${JSON.stringify(asset)}`);
};

export const getOriginalAspectRatio = ({
  item,
  asset,
}: {
  item: EditorStarterItem;
  asset: EditorStarterAsset | null;
}) => {
  // 아바타 오버레이(알파) 클립은 placement 에서 정한 박스 비율 자체가 의도된 비율이다.
  // 일반 영상은 native 해상도(asset) 비율로 락하는 게 맞지만, 아바타는 placement 박스를
  // native 비율로 강제하면 사용자가 맞춘 위치/크기 비율이 깨진다(리사이즈 시 박스가
  // 엉뚱한 크기로 점프). 따라서 현재 박스(item.width/height) 비율을 기준으로 한다.
  const isAlphaOverlay = item.type === 'video' && item.hasAlpha === true;
  const fromAsset =
    !isAlphaOverlay && asset ? getAspectRatioFromAsset(asset) : null;
  const baseAspectRatio = fromAsset ?? item.width / item.height;

  const crop = getCropFromItem(item);
  if (crop) {
    const cropWidth = 1 - crop.cropLeft - crop.cropRight;
    const cropHeight = 1 - crop.cropTop - crop.cropBottom;
    if (cropWidth > 0 && cropHeight > 0) {
      return baseAspectRatio * (cropWidth / cropHeight);
    }
  }

  return baseAspectRatio;
};
